import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Receipt } from 'lucide-react';
import { SurgeonTeamIcon, MedicalTrainingIcon, ResearchLabIcon } from './MedicalIcons';
import SocialShare from './SocialShare';
import { trackEvent } from '../../lib/analytics';

const fireCta = (action) => trackEvent('cta_click', { location: 'final_cta', action });

const FinalCTA = ({ onContactClick }) => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      setScrollProgress(Math.max(0, Math.min(1, 1 - (rect.top / window.innerHeight))));
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={sectionRef} data-testid="final-cta-section" className="relative min-h-screen flex items-center justify-center bg-slate-950 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/flexioss-product.png" alt="FlexiOss bone regenerative biomaterial product packaging" className="w-full h-full object-cover opacity-20" style={{ transform: `scale(${1 + scrollProgress * 0.1})` }} loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/50" />
      </div>
      <div className="container mx-auto px-6 relative z-10 text-center">
        <div className="max-w-4xl mx-auto space-y-10" style={{ opacity: scrollProgress, transform: `translateY(${(1 - scrollProgress) * 40}px)` }}>
          <div>
            <h2 className="text-5xl md:text-8xl font-black text-white mb-4 md:mb-6 leading-tight">FLEXIOSS</h2>
            <div className="text-3xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600">AVAILABLE NOW</div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => { fireCta('request_samples'); onContactClick('samples'); }} data-testid="final-cta-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-black px-8 md:px-16 py-6 md:py-8 text-lg md:text-2xl uppercase tracking-wider shadow-2xl shadow-blue-600/50 transition-all duration-300 hover:scale-110 w-full sm:w-auto"
            >
              Request Samples Now <ArrowRight className="ml-3 w-6 h-6 md:w-8 md:h-8" />
            </Button>
            <Button size="lg" onClick={() => { fireCta('request_pricing'); onContactClick('pricing'); }} data-testid="final-pricing-btn"
              className="bg-transparent border-2 border-[#E87A20] text-[#E87A20] hover:bg-[#E87A20] hover:text-white font-black px-8 md:px-16 py-6 md:py-8 text-lg md:text-2xl uppercase tracking-wider transition-all duration-300 hover:scale-110 w-full sm:w-auto"
            >
              Request Pricing <Receipt className="ml-3 w-6 h-6 md:w-8 md:h-8" />
            </Button>
          </div>

          {/* Referral & Programme CTAs */}
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto pt-4">
            <button onClick={() => { fireCta('refer_colleague'); onContactClick(); }} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 hover:border-blue-500 rounded-xl p-5 text-left transition-all duration-300 hover:-translate-y-1 group" data-testid="referral-cta">
              <SurgeonTeamIcon className="w-6 h-6 text-blue-400 mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Refer a Colleague</h4>
              <p className="text-slate-400 text-xs leading-relaxed">Know a surgeon who could benefit? Refer them and receive a complimentary demo kit.</p>
            </button>
            <button onClick={() => { fireCta('training_poland'); onContactClick(); }} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 hover:border-blue-500 rounded-xl p-5 text-left transition-all duration-300 hover:-translate-y-1 group" data-testid="training-cta">
              <MedicalTrainingIcon className="w-6 h-6 text-blue-400 mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Training in Poland</h4>
              <p className="text-slate-400 text-xs leading-relaxed">Heavy user? We'll sponsor your trip to Medical Inventi's R&D facility in Poland for hands-on training.</p>
            </button>
            <button onClick={() => { fireCta('research_collab'); onContactClick(); }} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 hover:border-blue-500 rounded-xl p-5 text-left transition-all duration-300 hover:-translate-y-1 group" data-testid="research-cta">
              <ResearchLabIcon className="w-6 h-6 text-blue-400 mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Research Collaboration</h4>
              <p className="text-slate-400 text-xs leading-relaxed">Got a research idea and protocol? We can help with funding, materials, and publication support.</p>
            </button>
          </div>

          <div className="pt-6 flex flex-col items-center gap-4">
            <div className="w-full max-w-3xl"><SocialShare pageKey="home" accent="#3b82f6" /></div>
            <p className="text-lg text-slate-400">Exclusively distributed by <span className="text-[#E87A20] font-bold">Fortschritt Medical Supplies Inc.</span></p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
